// services/everpro/everpro-shipping/rates/index.ts — fetchShippingRates per-brand via S2 proxy
import { withRetry } from '../../../../utils/retryLogic';
import { everproFetch } from '../../everpro-auth';
import type { EverproRatesData, GetRatesParams } from '../../everpro-types';
import { buildRatesBody } from './body.js';
import { tryResolveNotFound } from './fallback.js';

export async function fetchShippingRates(params: GetRatesParams, brandId: string): Promise<EverproRatesData> {
  if (!brandId) throw new Error('Everpro: brandId wajib');

  const body = buildRatesBody(params);

  const res = await withRetry(() => everproFetch(brandId, '/shipment/v2/rates', {
    method: 'POST',
    body: JSON.stringify(body),
  }), {
    maxRetries: 2,
    initialDelayMs: 1000,
    backoffMultiplier: 2,
    shouldRetry: (err) => {
      if (err instanceof TypeError) return true;
      return false;
    },
  });

  if (!res.ok) {
    const detail = await res.text().catch(() => '');

    let apiMessage = '';
    try {
      const errorJson = JSON.parse(detail);
      apiMessage = errorJson.message || '';
    } catch {
    }

    const resolved = await tryResolveNotFound(apiMessage, res.status, body, brandId, {
      destSubDistrict: params.destinationSubDistrictName,
      destDistrict: (params as any).destDistrictName,
      destCity: (params as any).destCityName,
      originSubDistrict: params.originSubDistrictName,
    });
    if (resolved) return resolved;

    if (apiMessage) {
      throw new Error(`Everpro rates: ${apiMessage}`);
    }

    throw new Error(`Everpro rates: ${res.status} ${res.statusText}${detail ? ' – ' + detail.slice(0, 120) : ''}`);
  }

  const json = await res.json();
  return (json?.data ?? {}) as EverproRatesData;
}
